/**
 * Forbidden Page Component
 * Displays 403 Forbidden error page when user lacks permissions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Title1,
  Title3,
  Body1,
  Button,
} from '@fluentui/react-components';
import { Home24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    ...shorthands.padding(tokens.spacingVerticalXXXL),
    textAlign: 'center',
  },
  title: {
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  subtitle: {
    marginBottom: tokens.spacingVerticalM,
  },
  message: {
    maxWidth: '600px',
    marginBottom: tokens.spacingVerticalL,
  },
  actions: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    marginTop: tokens.spacingVerticalL,
  },
});

/**
 * Forbidden Page Component
 * Displays 403 Forbidden error page when user attempts to access
 * a page they don't have permission to view.
 *
 * @example
 * ```tsx
 * <Route path="/admin" element={<ForbiddenPage />} />
 * ```
 */
export const ForbiddenPage: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.container} data-testid="forbidden-page">
      <Title1 className={styles.title}>403 Forbidden</Title1>
      <Title3 className={styles.subtitle}>Access Denied</Title3>
      <Body1 className={styles.message}>
        You do not have permission to access this page.
        <br />
        Please contact your administrator if you believe this is an error.
      </Body1>
      <div className={styles.actions}>
        <Link to="/">
          <Button appearance="primary" icon={<Home24Regular />}>
            Go to Home
          </Button>
        </Link>
      </div>
    </div>
  );
};
